import moment from 'moment';
import React from 'react';
import styled from 'styled-components';
import {Icon} from '@forecast-it/design-system';

const SUBMITTED_TIMESHEETS_COLOR = '#d32f2f';

const DotIndicator = styled.span`
	position: absolute;
	top: 10px;
	${({position}) => (position === 'left' ? 'left: 10px;' : 'right: 10px;')}
	width: 6px;
	height: 6px;
	background-color: ${SUBMITTED_TIMESHEETS_COLOR};
	border-radius: 50%;
`;

const WeekIndicatorWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: 5px;
`;

const SubmittedCountBadge = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 6px;
	border-radius: 999px;
	background: ${SUBMITTED_TIMESHEETS_COLOR};
	color: white;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	white-space: nowrap;
`;

const normalizeWeekIdentity = value => {
	if (!value) {
		return null;
	}

	return moment(value).format('YYYY-MM-DD');
};

export const getWeekIdentityFromMoment = weekMoment => weekMoment.clone().startOf('week').format('YYYY-MM-DD');

// A week key is a calendar date, compared against the picker's local dates. `new Date('YYYY-MM-DD')`
// reads it as UTC midnight instead, which falls on the previous day west of UTC and after local
// midnight east of it, so a week could flag itself as earlier or later than the week being viewed.
const weekStartToLocalDate = weekStart => moment(weekStart, 'YYYY-MM-DD').toDate();

export const getSubmittedTimesheetsByWeek = submittedWeeks => {
	return (submittedWeeks || []).reduce((acc, week) => {
		if (week?.weekStartDate) {
			acc[normalizeWeekIdentity(week.weekStartDate)] = week.submittedCount ?? 0;
		}
		return acc;
	}, {});
};

const formatSubmittedCount = submittedCount => {
	if (submittedCount > 999) {
		return '999+';
	}

	return submittedCount;
};

export const getWeekIndicator = submittedCount => {
	if (submittedCount === 0 || submittedCount === null || submittedCount === undefined) {
		return (
			<WeekIndicatorWrapper>
				<Icon color={'success'} icon={'checkCircle'} size={'m'} />
			</WeekIndicatorWrapper>
		);
	}

	if (submittedCount > 0) {
		return (
			<WeekIndicatorWrapper>
				<SubmittedCountBadge>{formatSubmittedCount(submittedCount)}</SubmittedCountBadge>
			</WeekIndicatorWrapper>
		);
	}

	return null;
};

export const createCalendarWeekIndicatorRenderer = submittedTimesheetsByWeek => weekstart => {
	const normalizedWeekStart = normalizeWeekIdentity(weekstart);
	return getWeekIndicator(submittedTimesheetsByWeek[normalizedWeekStart]);
};

export const createMonthNavPreviousIndicatorRenderer = submittedTimesheetsByWeek => currentMonthDate => {
	const year = currentMonthDate.getFullYear();
	const month = currentMonthDate.getMonth();
	const monthStart = new Date(year, month, 1);

	const hasIndicator = Object.keys(submittedTimesheetsByWeek).some(weekStart => {
		const submittedCount = submittedTimesheetsByWeek[weekStart];
		if (submittedCount <= 0) {
			return false;
		}

		const weekStartDate = weekStartToLocalDate(weekStart);
		return weekStartDate < monthStart;
	});

	return hasIndicator ? <DotIndicator position="left" /> : null;
};

export const createMonthNavNextIndicatorRenderer = submittedTimesheetsByWeek => currentMonthDate => {
	const year = currentMonthDate.getFullYear();
	const month = currentMonthDate.getMonth();
	const monthEnd = new Date(year, month + 1, 0);

	const hasIndicator = Object.keys(submittedTimesheetsByWeek).some(weekStart => {
		const submittedCount = submittedTimesheetsByWeek[weekStart];
		if (submittedCount <= 0) {
			return false;
		}

		const weekStartDate = weekStartToLocalDate(weekStart);
		return weekStartDate > monthEnd;
	});

	return hasIndicator ? <DotIndicator position="right" /> : null;
};

export const createWeekNavPreviousIndicatorRenderer = submittedTimesheetsByWeek => currentWeekDate => {
	const currentMonth = currentWeekDate.getMonth();
	const currentYear = currentWeekDate.getFullYear();

	const hasIndicator = Object.keys(submittedTimesheetsByWeek).some(weekStart => {
		const submittedCount = submittedTimesheetsByWeek[weekStart];
		if (submittedCount <= 0) {
			return false;
		}

		const weekStartDate = weekStartToLocalDate(weekStart);
		if (weekStartDate.getMonth() !== currentMonth || weekStartDate.getFullYear() !== currentYear) {
			return false;
		}

		return weekStartDate < currentWeekDate;
	});

	return hasIndicator ? <DotIndicator position="left" /> : null;
};

export const createWeekNavNextIndicatorRenderer = submittedTimesheetsByWeek => currentWeekDate => {
	const currentMonth = currentWeekDate.getMonth();
	const currentYear = currentWeekDate.getFullYear();

	const hasIndicator = Object.keys(submittedTimesheetsByWeek).some(weekStart => {
		const submittedCount = submittedTimesheetsByWeek[weekStart];
		if (submittedCount <= 0) {
			return false;
		}

		const weekStartDate = weekStartToLocalDate(weekStart);
		if (weekStartDate.getMonth() !== currentMonth || weekStartDate.getFullYear() !== currentYear) {
			return false;
		}

		return weekStartDate > currentWeekDate;
	});

	return hasIndicator ? <DotIndicator position="right" /> : null;
};

export const SubmittedTimesheetsWeekIndicator = ({submittedCount}) => {
	return getWeekIndicator(submittedCount);
};

export const getSubmittedTimesheetsWeekEntry = (submittedTimesheetsByWeek, weekStartDate) => {
	const normalizedWeekStart = normalizeWeekIdentity(weekStartDate);
	if (!normalizedWeekStart) {
		return null;
	}

	const submittedCount = submittedTimesheetsByWeek[normalizedWeekStart];
	if (submittedCount == null) {
		return null;
	}

	return {submittedCount};
};
